import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { Panel, Button, Input, ConfirmModal } from '@/components/ui';

interface UserRow {
  user_id: string;
  email: string | null;
  status: 'pending' | 'approved' | 'rejected';
  // work-role from user_profiles.work_role (renamed from `role` 2026-05-25)
  // — surfaced in pending-queue disclosure. UNTRUSTED user input.
  work_role: string | null;
  // JWT-derived admin claim from auth.users.app_metadata.role — used for the
  // ADMIN badge + promote/demote button branching. Hydrated by listUsers.
  // The ONLY field on this row that grants platform privilege.
  admin_role: string | null;
  approved_at: string | null;
  // The admin who invited this user; resolved to admin email client-side for
  // the "invited by Admin X" tag (research/phase-4.md § 11 risk #5 — degraded
  // "<unknown>" shown if the admin row is no longer in the users[] list).
  invited_by: string | null;
  invited_at: string | null;
  created_at: string;
  // Phase 1 raw 6-answer fields — surfaced in the pending-queue disclosure
  // when status === 'pending'. signup-billing-mvp Phase 4 Session 3.
  firm_name: string | null;
  asset_classes: string | null;
  markets: string | null;
  focus: string | null;
  volume: string | null;
  onboarding_completed: boolean;
  founding_partner: boolean;
}

const modalTitles: Record<string, string> = {
  approve: 'Approve User',
  reject: 'Reject User',
  revoke: 'Revoke Access',
  promote: 'Grant Admin',
  demote: 'Remove Admin',
};

const modalMessages: Record<string, string> = {
  approve: 'This user will be granted access to the platform.',
  reject: 'This user will be denied access to the platform.',
  revoke: 'This user will be blocked from the platform.',
  promote: 'This user will be able to manage users and models.',
  demote: 'This user will lose admin privileges.',
};

const modalLabels: Record<string, string> = {
  approve: 'Approve',
  reject: 'Reject',
  revoke: 'Revoke',
  promote: 'Make Admin',
  demote: 'Remove Admin',
};

export function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<
    'all' | 'pending' | 'approved' | 'rejected'
  >('all');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [modalAction, setModalAction] = useState<{
    type: string;
    user: UserRow;
  } | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  // Founding-Partner toggle state for the approve-dialog children slot. Reset
  // after every confirm/close so the next approve starts unchecked.
  // signup-billing-mvp Phase 4 Session 3.
  const [approveFoundingPartner, setApproveFoundingPartner] = useState(false);
  // String-backed controlled <input type="number"> for the COGS-grant override.
  // Default 1996 COGS = founding-partner-tier derived grant (base_tier_price
  // $499 × usage_multiplier 4.0 = $1996 → 1996 COGS at base_ratio=20.0
  // ≈ $99.80 of Anthropic spend). parseInt at submit; null on parse failure
  // or non-positive (server's Zod refinement rejects ≤ 0 with 400).
  const [approveGrantCogs, setApproveGrantCogs] = useState<string>('1996');

  const currentUserId = user?.id;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const params = filter === 'all' ? '' : `?status=${filter}`;
    const res = await api.get<{ data: UserRow[] }>(`/api/admin/users${params}`);
    setUsers(res.data);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    const firstName = inviteFirstName.trim();
    const lastName = inviteLastName.trim();
    if (!email || !firstName || !lastName) {
      setError('First name, last name, and email are required');
      return;
    }
    setInviting(true);
    setError(null);
    try {
      await api.post('/api/admin/users/invite', { email, firstName, lastName });
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setShowInvite(false);
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite');
    }
    setInviting(false);
  };

  const handleConfirm = async () => {
    if (!modalAction) return;
    const { type, user: targetUser } = modalAction;
    const id = targetUser.user_id;

    const endpoints: Record<string, { path: string; body?: object }> = {
      approve: {
        path: `/api/admin/users/${id}/approve`,
        body: {
          founding_partner_flag: approveFoundingPartner,
          founding_partner_grant_cogs: approveFoundingPartner
            ? (() => {
                const parsed = parseInt(approveGrantCogs, 10);
                return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
              })()
            : null,
        },
      },
      reject: {
        path: `/api/admin/users/${id}/reject`,
        body: { reason: revokeReason },
      },
      revoke: {
        path: `/api/admin/users/${id}/revoke`,
        body: { reason: revokeReason },
      },
      promote: { path: `/api/admin/users/${id}/promote` },
      demote: { path: `/api/admin/users/${id}/demote` },
    };

    try {
      await api.post(endpoints[type].path, endpoints[type].body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
    setModalAction(null);
    setRevokeReason('');
    setApproveFoundingPartner(false);
    setApproveGrantCogs('1996');
    fetchUsers();
  };

  const pendingCount = users.filter((u) => u.status === 'pending').length;

  const statusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-accent';
      case 'pending':
        return 'text-warn';
      case 'rejected':
        return 'text-danger';
      default:
        return 'text-text-muted';
    }
  };

  return (
    <Panel
      title="Users"
      badge={pendingCount > 0 ? `${pendingCount} pending` : undefined}
      right={
        <Button variant="primary" onClick={() => setShowInvite(!showInvite)}>
          Invite User
        </Button>
      }
    >
      {/* Invite form */}
      {showInvite && (
        <form
          onSubmit={handleInvite}
          className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-input-bg border border-panel-border rounded"
        >
          <Input
            value={inviteFirstName}
            onChange={(e) => setInviteFirstName(e.target.value)}
            placeholder="First name"
            type="text"
            aria-label="Invitee first name"
            autoComplete="given-name"
          />
          <Input
            value={inviteLastName}
            onChange={(e) => setInviteLastName(e.target.value)}
            placeholder="Last name"
            type="text"
            aria-label="Invitee last name"
            autoComplete="family-name"
          />
          <Input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@company.com"
            type="email"
            aria-label="Invitee email"
            autoComplete="email"
          />
          <Button variant="primary" type="submit" disabled={inviting}>
            {inviting ? 'Sending...' : 'Send Invite'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setShowInvite(false);
              setError(null);
            }}
          >
            Cancel
          </Button>
        </form>
      )}
      {error && (
        <div className="px-3 py-2 mb-3 bg-danger/[0.06] border border-danger/13 rounded text-danger text-xs font-mono">
          {error}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-1 mb-3">
        {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs font-mono rounded cursor-pointer transition-colors ${
              filter === f
                ? 'bg-accent-dim text-accent border border-accent/15'
                : 'text-text-dim hover:text-text-muted bg-transparent border border-transparent'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* User list */}
      {loading ? (
        <div className="py-8 text-center text-text-dim text-xs font-mono">
          Loading users...
        </div>
      ) : users.length === 0 ? (
        <div className="py-8 text-center text-text-dim text-xs font-mono">
          No users found
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {users.map((u) => {
            const isSelf = u.user_id === currentUserId;
            const isPending = u.status === 'pending';
            // Resolve invited-by admin email from the same users[] response.
            // Degraded "<unknown>" when the inviting admin row is no longer
            // in the list (research/phase-4.md § 11 risk #5).
            const invitedByEmail = u.invited_by
              ? users.find((v) => v.user_id === u.invited_by)?.email ??
                '<unknown>'
              : null;
            const sourceTagDate = u.invited_at ?? u.created_at;

            return (
              <div
                key={u.user_id}
                className="flex flex-col gap-2 px-3 py-2.5 bg-input-bg border border-panel-border rounded"
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center">
                      <span className="text-sm font-mono text-text-bright">
                        {u.email}
                      </span>
                      {u.admin_role === 'admin' && (
                        <span className="ml-2 px-1.5 py-0.5 text-micro font-mono text-accent bg-accent/8 rounded-[2px]">
                          ADMIN
                        </span>
                      )}
                      {isSelf && (
                        <span className="ml-2 text-xs font-mono text-text-dim">
                          You
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-mono font-semibold ${statusColor(
                          u.status
                        )}`}
                      >
                        {u.status.toUpperCase()}
                      </span>
                      <span className="text-xs font-mono text-text-dim">
                        {new Date(u.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {/*
                      Pending-queue invited-vs-self tag (signup-billing-mvp
                      Phase 4 Session 3 — spec AC L399). Renders only for
                      pending rows. invitedByEmail derived above.
                    */}
                    {isPending && (
                      <div
                        className="text-xs font-mono text-text-muted"
                        data-testid="pending-source-tag"
                      >
                        {u.invited_by
                          ? `invited by ${invitedByEmail} (${new Date(
                              sourceTagDate
                            ).toLocaleDateString()})`
                          : `self-signed-up (${new Date(
                              sourceTagDate
                            ).toLocaleDateString()})`}
                      </div>
                    )}
                  </div>

                  {!isSelf && (
                    <div className="flex items-center gap-1.5">
                      {u.status === 'pending' && (
                        <>
                          <Button
                            variant="primary"
                            onClick={() =>
                              setModalAction({ type: 'approve', user: u })
                            }
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() =>
                              setModalAction({ type: 'reject', user: u })
                            }
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {u.status === 'approved' && (
                        <>
                          <Button
                            variant="danger"
                            onClick={() =>
                              setModalAction({ type: 'revoke', user: u })
                            }
                          >
                            Revoke
                          </Button>
                          {u.admin_role === 'admin' ? (
                            <Button
                              variant="danger"
                              onClick={() =>
                                setModalAction({ type: 'demote', user: u })
                              }
                            >
                              Remove Admin
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={() =>
                                setModalAction({ type: 'promote', user: u })
                              }
                            >
                              Make Admin
                            </Button>
                          )}
                        </>
                      )}
                      {u.status === 'rejected' && (
                        <Button
                          variant="primary"
                          onClick={() =>
                            setModalAction({ type: 'approve', user: u })
                          }
                        >
                          Approve
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/*
                  Pending-queue 6-answer disclosure (signup-billing-mvp Phase 4
                  Session 3 — spec AC L399). Default-collapsed <details> so the
                  list stays scannable; admin clicks to expand the verbatim
                  onboarding answers. Invited users who haven't completed the
                  wizard yet (onboarding_completed === false) get a friendly
                  placeholder instead of 6 empty rows.
                */}
                {isPending && (
                  <details className="pl-1" data-testid="pending-disclosure">
                    <summary className="text-xs font-mono text-text-muted cursor-pointer select-none">
                      Onboarding answers
                    </summary>
                    <div className="mt-2 flex flex-col gap-1">
                      {u.onboarding_completed ? (
                        <>
                          {(
                            [
                              ['firm_name', 'firm_name', u.firm_name],
                              ['work_role', 'work_role', u.work_role],
                              [
                                'asset_classes',
                                'asset_classes',
                                u.asset_classes,
                              ],
                              ['markets', 'markets', u.markets],
                              ['focus', 'focus', u.focus],
                              ['volume', 'volume', u.volume],
                            ] as ReadonlyArray<
                              readonly [string, string, string | null]
                            >
                          ).map(([key, label, value]) => (
                            <div
                              key={key}
                              className="flex items-baseline gap-2"
                              data-testid={`disclosure-field-${key}`}
                            >
                              <span className="font-mono text-xs text-text-muted w-32 shrink-0">
                                {label}
                              </span>
                              <span className="text-sm text-text font-body">
                                {value ?? '—'}
                              </span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div
                          className="text-xs font-mono text-text-muted"
                          data-testid="disclosure-awaiting"
                        >
                          Awaiting onboarding — no answers yet
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation modal */}
      <ConfirmModal
        open={!!modalAction}
        onClose={() => {
          setModalAction(null);
          setRevokeReason('');
          setApproveFoundingPartner(false);
          setApproveGrantCogs('1996');
        }}
        onConfirm={handleConfirm}
        title={modalAction ? modalTitles[modalAction.type] : ''}
        danger={
          modalAction
            ? ['revoke', 'demote', 'reject'].includes(modalAction.type)
            : false
        }
        confirmLabel={modalAction ? modalLabels[modalAction.type] : 'Confirm'}
      >
        <p className="text-sm text-text font-mono mb-3">
          {modalAction
            ? `${modalMessages[modalAction.type]} (${modalAction.user.email})`
            : ''}
        </p>
        {modalAction?.type === 'revoke' && (
          <input
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Reason (optional)"
            className="w-full py-2 px-2.5 bg-input-bg border border-input-border rounded-[2px] text-text-bright font-mono text-sm outline-none"
          />
        )}
        {/*
          Approve-dialog Founding-Partner toggle (signup-billing-mvp Phase 4
          Session 3 — spec AC L400). Default unchecked. When checked, reveals a
          numeric COGS-grant override (defaulting to the FP-tier derived grant
          1996 COGS ≈ $99.80 of Anthropic spend at base_ratio=20.0). Sends
          `{founding_partner_flag, founding_partner_grant_cogs}` in the request
          body — see handleConfirm's `endpoints.approve.body`.
        */}
        {modalAction?.type === 'approve' && (
          <div className="flex flex-col gap-3" data-testid="approve-fp-block">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={approveFoundingPartner}
                onChange={(e) => setApproveFoundingPartner(e.target.checked)}
                data-testid="approve-fp-toggle"
                aria-label="Mark as Founding Partner"
                className="w-4 h-4 accent-accent cursor-pointer"
              />
              <span className="text-sm text-text font-mono">
                Mark as Founding Partner
              </span>
            </label>
            {approveFoundingPartner && (
              <div
                className="flex flex-col gap-1.5"
                data-testid="approve-fp-grant-block"
              >
                <label
                  htmlFor="approve-fp-grant-cogs"
                  className="text-xs font-mono text-text-muted uppercase tracking-[0.5px]"
                >
                  Monthly allowance (COGS)
                </label>
                <input
                  id="approve-fp-grant-cogs"
                  type="number"
                  min={1}
                  step={1}
                  value={approveGrantCogs}
                  onChange={(e) => setApproveGrantCogs(e.target.value)}
                  data-testid="approve-fp-grant-input"
                  aria-label="Monthly allowance in COGS"
                  className="w-full py-2 px-2.5 bg-input-bg border border-input-border rounded-[2px] text-text-bright font-mono text-sm outline-none focus:border-accent/30"
                />
                <p className="text-xs font-mono text-text-muted">
                  = 1996 COGS &asymp; $99.80 of Anthropic spend at
                  base_ratio=20.0 (the founding-partner tier default; override
                  to set custom).
                </p>
              </div>
            )}
          </div>
        )}
      </ConfirmModal>
    </Panel>
  );
}
